import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, 
    ChevronLeft, 
    ChevronRight, 
    X, 
    VolumeX, 
    Wind, 
    Snowflake, 
    RotateCcw, 
    BatteryLow,
    HelpCircle,
    Check,
    Heart,
    Bookmark,
    Library,
    Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { SelectionCard, BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ProgressDots } from '../components/brand/BrandProgress';
import { SafeIndicator } from '../components/brand/BrandIndicators';
import { usePremiumFeature } from '../components/PremiumFeature';

const iconMap = {
    'volume-x': VolumeX,
    'wind': Wind,
    'snowflake': Snowflake,
    'rotate-ccw': RotateCcw,
    'battery-low': BatteryLow,
    'help-circle': HelpCircle
};

const Regulate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { api, canAccess } = useAuth();
    const { checkAccess } = usePremiumFeature();
    
    const [step, setStep] = useState('select');
    const [overwhelmTypes, setOverwhelmTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [actions, setActions] = useState([]);
    const [actionIndex, setActionIndex] = useState(0);
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Handler for saving favourite grounding step (SUPPORT tier trigger)
    const handleSaveFavourite = () => {
        checkAccess('save_favourites', () => {
            toast.success('Saved to favourites!', {
                description: 'You can find this in your saved steps.'
            });
        });
    };

    // Handler for accessing extended library (SUPPORT tier trigger)
    const handleExtendedLibrary = () => {
        checkAccess('extended_library', () => {
            toast.success('Opening extended library...');
        });
    };

    // Check if coming from onboarding with pre-selected type
    useEffect(() => {
        if (location.state?.fromOnboarding && location.state?.selectedType) {
            setSelectedType(location.state.selectedType);
            setActions(location.state.actions || []);
            setSessionId(location.state.sessionId);
            setActionIndex(0);
            setStep('regulate');
            // Clear the location state to prevent re-triggering
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchOverwhelmTypes = async () => {
            try {
                const response = await api().get('/regulation/overwhelm-types');
                setOverwhelmTypes(response.data);
            } catch (error) {
                toast.error('Unable to load options');
            }
        };
        fetchOverwhelmTypes();
    }, [api]);

    const handleSelectType = async (type) => {
        setSelectedType(type);
        setLoading(true);
        
        try {
            const [actionsRes, sessionRes] = await Promise.all([
                api().get(`/regulation/actions/${type.id}`),
                api().post('/regulation/sessions', {
                    overwhelm_type: type.id,
                    relational_context: 'solo',
                    actions_completed: 0
                })
            ]);
            
            setActions(actionsRes.data);
            setSessionId(sessionRes.data.id);
            setActionIndex(0);
            setStep('regulate');
        } catch (error) {
            toast.error('Unable to start session');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        const newIndex = actionIndex + 1;
        
        if (newIndex >= actions.length) {
            try {
                await api().put(`/regulation/sessions/${sessionId}?actions_completed=${actions.length}`);
                setStep('complete');
            } catch (error) {
                console.error('Failed to update session:', error);
            }
        } else {
            setActionIndex(newIndex);
            try {
                await api().put(`/regulation/sessions/${sessionId}?actions_completed=${newIndex}`);
            } catch (error) {
                console.error('Failed to update progress:', error);
            }
        }
    };

    const handlePrevious = () => {
        if (actionIndex > 0) {
            setActionIndex(actionIndex - 1);
        }
    };

    const handleExit = () => {
        navigate('/home');
    };

    const handleRestart = () => {
        setStep('select');
        setSelectedType(null);
        setActions([]);
        setActionIndex(0);
        setSessionId(null);
    };

    // ═══════════════════════════════════════════════════════════════
    // SELECT OVERWHELM TYPE SCREEN
    // ═══════════════════════════════════════════════════════════════
    if (step === 'select') {
        return (
            <div className="min-h-screen bg-transparent">
                {/* Minimal header */}
                <header className="bg-transparent">
                    <div className="max-w-xl mx-auto px-6 pt-8 pb-4">
                        <TertiaryButton 
                            onClick={() => navigate('/home')}
                            data-testid="back-btn"
                            underline={false}
                            className="text-[#7D8E95] hover:text-[#2A8585]"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Back</span>
                        </TertiaryButton>
                    </div>
                </header>

                <main className="max-w-xl mx-auto px-6 pb-16">
                    {/* Spacious intro section */}
                    <div className="text-center py-12 brand-fade-in">
                        <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-6">
                            <Heart className="w-6 h-6" />
                        </IconContainer>
                        <Heading as="h1" size="text-2xl md:text-3xl" className="mb-4">
                            What are you feeling?
                        </Heading>
                        <BodyText size="lg" className="max-w-sm mx-auto leading-relaxed">
                            Choose the one that feels closest. 
                            <br />
                            <span className="text-[#1D6B6B]">There's no wrong answer.</span>
                        </BodyText>
                    </div>

                    {/* Selection cards with generous spacing */}
                    <div className="space-y-4">
                        {overwhelmTypes.map((type, index) => {
                            const IconComponent = iconMap[type.icon] || Wind;
                            return (
                                <SelectionCard 
                                    key={type.id}
                                    className={`brand-slide-up ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    onClick={() => handleSelectType(type)}
                                    data-testid={`overwhelm-type-${type.id}`}
                                >
                                    <div className="flex items-center gap-5">
                                        <IconContainer variant="sage" size="lg" useGradient={true}>
                                            <IconComponent className="w-5 h-5" />
                                        </IconContainer>
                                        <div className="flex-1 min-w-0">
                                            <Heading as="h3" size="text-base md:text-lg" weight="medium" className="mb-0.5">
                                                {type.name}
                                            </Heading>
                                            <BodyText className="text-sm leading-relaxed">
                                                {type.description}
                                            </BodyText>
                                        </div>
                                    </div>
                                </SelectionCard>
                            );
                        })}
                    </div>
                    
                    {/* Extended Library Access - SUPPORT tier trigger */}
                    <div className="mt-8 text-center brand-fade-in" style={{ animationDelay: '400ms' }}>
                        <button
                            onClick={handleExtendedLibrary}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium text-[#7D8E95] hover:text-[#2A8585] bg-[#FFBB98]/30 hover:bg-[#FFBB98]/50 transition-all duration-300"
                            data-testid="extended-library-btn"
                        >
                            <Library className="w-4 h-4" />
                            Explore extended library
                            {!canAccess('extended_library') && (
                                <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-[#7D8E95]/20 text-[#7D8E95] flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" />
                                    Support
                                </span>
                            )}
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════
    // REGULATION IN PROGRESS - ONE ACTION AT A TIME
    // ═══════════════════════════════════════════════════════════════
    if (step === 'regulate') {
        const IconComponent = iconMap[selectedType?.icon] || Wind;
        const isLastStep = actionIndex === actions.length - 1;
        
        return (
            <div className="min-h-screen bg-transparent flex flex-col">
                {/* Minimal, calming header */}
                <header className="bg-white/70/80 backdrop-blur-sm">
                    <div className="max-w-xl mx-auto px-6 py-5 flex items-center justify-between">
                        {/* Small overwhelm type indicator */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[rgba(52,70,72,0.15)] flex items-center justify-center">
                                <IconComponent className="w-4 h-4 text-[#1D6B6B]" />
                            </div>
                            <SmallText className="font-medium text-[#2A8585]">
                                {selectedType?.name}
                            </SmallText>
                        </div>
                        
                        {/* Exit button */}
                        <button 
                            onClick={handleExit}
                            className="w-9 h-9 rounded-full flex items-center justify-center text-[#7D8E95] hover:text-[#2A8585] hover:bg-[#FFBB98] transition-all duration-300"
                            data-testid="exit-btn"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </header>

                {/* Main content - centered and spacious */}
                <main className="flex-1 flex flex-col max-w-xl mx-auto w-full px-6">
                    
                    {/* Step indicator - subtle and supportive */}
                    <div className="text-center pt-8 pb-4 brand-fade-in">
                        <SmallText className="text-[#1D6B6B] font-medium">
                            Step {actionIndex + 1} of {actions.length}
                        </SmallText>
                    </div>

                    {/* Action Card - the heart of the experience */}
                    <div className="flex-1 flex items-center justify-center py-8">
                        <BrandCard 
                            variant="elevated" 
                            padding="xl"
                            className="w-full max-w-md brand-scale-in rounded-3xl"
                            key={actionIndex}
                        >
                            <div className="text-center py-4">
                                <BodyText 
                                    size="lg" 
                                    className="text-[#1D6B6B] leading-relaxed text-lg md:text-xl font-medium"
                                >
                                    {actions[actionIndex]}
                                </BodyText>
                                
                                {/* Save Favourite Button - SUPPORT tier trigger */}
                                <button
                                    onClick={handleSaveFavourite}
                                    className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-[#7D8E95] hover:text-[#2A8585] hover:bg-[#FFBB98]/50 transition-all duration-300"
                                    data-testid="save-favourite-btn"
                                >
                                    <Bookmark className="w-4 h-4" />
                                    Save to favourites
                                    {!canAccess('save_favourites') && (
                                        <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-[#7D8E95]/20 text-[#7D8E95]">
                                            <Sparkles className="w-3 h-3 inline" />
                                        </span>
                                    )}
                                </button>
                            </div>
                        </BrandCard>
                    </div>

                    {/* Navigation - clear and calm */}
                    <div className="pb-12 pt-4 space-y-8">
                        {/* Progress dots */}
                        <div className="flex justify-center">
                            <ProgressDots 
                                total={actions.length} 
                                current={actionIndex} 
                            />
                        </div>
                        
                        {/* Navigation buttons */}
                        <div className="flex items-center justify-center gap-4">
                            {/* Previous - subtle */}
                            <button
                                onClick={handlePrevious}
                                disabled={actionIndex === 0}
                                className={`
                                    w-12 h-12 rounded-full flex items-center justify-center
                                    transition-all duration-300
                                    ${actionIndex === 0 
                                        ? 'text-[#7D8E95] cursor-not-allowed' 
                                        : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-[#FFBB98]'
                                    }
                                `}
                                data-testid="prev-step-btn"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            
                            {/* Next Step - the primary action */}
                            <PrimaryButton
                                onClick={handleNext}
                                size="lg"
                                className="px-8 min-w-[160px]"
                                data-testid="next-step-btn"
                            >
                                {isLastStep ? (
                                    <span className="flex items-center gap-2">
                                        <Check className="w-4 h-4" />
                                        Complete
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        Next Step
                                        <ChevronRight className="w-4 h-4" />
                                    </span>
                                )}
                            </PrimaryButton>
                            
                            {/* Spacer for balance */}
                            <div className="w-12 h-12" />
                        </div>
                        
                        {/* Supportive text */}
                        <div className="text-center">
                            <SmallText className="text-[#7D8E95]">
                                Take your time. Move when you're ready.
                            </SmallText>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════
    // COMPLETION SCREEN
    // ═══════════════════════════════════════════════════════════════
    if (step === 'complete') {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center p-6">
                <div className="max-w-md w-full text-center brand-scale-in">
                    {/* Gentle success indicator */}
                    <div 
                        className="w-24 h-24 rounded-full mx-auto mb-10 flex items-center justify-center"
                        style={{
                            background: 'linear-gradient(135deg, rgba(52,70,72,0.2) 0%, rgba(52,70,72,0.1) 100%)',
                            border: '2px solid rgba(52,70,72,0.3)'
                        }}
                    >
                        <Check className="w-10 h-10 text-[#1D6B6B]" />
                    </div>
                    
                    <Heading as="h2" size="text-2xl md:text-3xl" className="mb-4">
                        Well done
                    </Heading>
                    
                    <BodyText size="lg" className="mb-4 leading-relaxed">
                        You completed all {actions.length} grounding steps.
                    </BodyText>
                    
                    <BodyText className="text-[#1D6B6B] mb-12">
                        Take a moment to notice how you feel now.
                    </BodyText>
                    
                    {/* Action buttons with generous spacing */}
                    <div className="space-y-4">
                        <PrimaryButton 
                            onClick={handleRestart}
                            className="w-full"
                            size="xl"
                            data-testid="do-another-btn"
                        >
                            Do Another Round
                        </PrimaryButton>
                        <SecondaryButton 
                            onClick={() => navigate('/home')}
                            className="w-full"
                            size="xl"
                            data-testid="back-to-dashboard-btn"
                        >
                            Back to Dashboard
                        </SecondaryButton>
                    </div>
                    
                    {/* Closing affirmation */}
                    <SmallText className="mt-12 text-[#7D8E95]">
                        You showed up for yourself. That matters.
                    </SmallText>
                </div>
            </div>
        );
    }

    return null;
};

export default Regulate;
